import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ujmsViolations, ujmsIssueTrafficCitation, ujmsTrafficCitations, ujmsOpenCitationTicket } from "../../services/ujmsApi";
import { Panel, thCls, tdCls, btnPrimary, btnGhost, inputCls, selectCls, Field } from "../ui";
import { TicketCheck, Printer } from "lucide-react";

const BLANK = { first_name: "", last_name: "", dob: "", license_no: "", vehicle_plate: "",
  vehicle_state: "", vehicle_desc: "", violation_code: "", location: "", notes: "" };

export default function ECitation() {
  const [violations, setViolations] = useState([]);
  const [f, setF] = useState(BLANK);
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => ujmsTrafficCitations().then((r) => setList(r.data)).catch(() => {});
  useEffect(() => {
    ujmsViolations().then((r) => { setViolations(r.data); setF((p) => ({ ...p, violation_code: r.data[0]?.code || "" })); }).catch(() => {});
    load();
  }, []);
  const v = violations.find((x) => x.code === f.violation_code);
  const valid = f.first_name.trim() && f.last_name.trim() && f.location.trim() && f.violation_code;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await ujmsIssueTrafficCitation(f);
      toast.success(`Citation ${r.data.citation_number} filed — case ${r.data.case_number} created`
        + (r.data.court_date ? `, appearance auto-set for ${r.data.court_date}` : ""));
      setF({ ...BLANK, violation_code: violations[0]?.code || "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to file citation"); }
    setBusy(false);
  };
  return (
    <div className="space-y-5" data-testid="ecitation-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">e-Citation</h1>
        <p className="text-sm text-slate-500">Issue a traffic citation in seconds — filing it automatically opens a Traffic (TR) case, dockets the violation, and assesses any scheduled fine.</p>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <Panel title="New citation" testId="cit-form-panel">
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Violator first name" required><input className={inputCls} value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="cit-first" /></Field>
              <Field label="Last name" required><input className={inputCls} value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="cit-last" /></Field>
              <Field label="Date of birth"><input type="date" className={inputCls} value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} data-testid="cit-dob" /></Field>
              <Field label="Driver's license #"><input className={inputCls} value={f.license_no} onChange={(e) => setF({ ...f, license_no: e.target.value })} data-testid="cit-license" /></Field>
              <Field label="Vehicle plate"><input className={inputCls} value={f.vehicle_plate} onChange={(e) => setF({ ...f, vehicle_plate: e.target.value.toUpperCase() })} data-testid="cit-plate" /></Field>
              <Field label="Vehicle description"><input className={inputCls} placeholder="e.g. Silver Honda Accord" value={f.vehicle_desc} onChange={(e) => setF({ ...f, vehicle_desc: e.target.value })} data-testid="cit-vehicle" /></Field>
            </div>
            <Field label="Violation" required>
              <select className={selectCls} value={f.violation_code} onChange={(e) => setF({ ...f, violation_code: e.target.value })} data-testid="cit-violation">
                {violations.map((x) => <option key={x.code} value={x.code}>{`${x.code} — ${x.label}${x.fine ? ` ($${x.fine.toFixed(2)})` : " (must appear)"}`}</option>)}
              </select>
            </Field>
            {v && (
              <div className="text-xs border border-slate-200 rounded-sm px-2.5 py-2 bg-slate-50 flex justify-between" data-testid="cit-fine-info">
                <span>{v.subtype}{v.must_appear ? " · court appearance required" : ""}</span>
                <span className="font-mono font-bold">{v.fine ? `$${v.fine.toFixed(2)}` : "Fine set by Court"}</span>
              </div>
            )}
            <Field label="Location of violation" required><input className={inputCls} placeholder="e.g. NB Route 9 at Mile 42" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="cit-location" /></Field>
            <Field label="Officer notes"><textarea className={inputCls} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="cit-notes" /></Field>
            <button className={btnPrimary} disabled={!valid || busy} onClick={submit} data-testid="cit-submit-btn">
              <TicketCheck className="w-4 h-4" /> {busy ? "Filing…" : "File Citation & Open TR Case"}
            </button>
          </div>
        </Panel>
        <Panel title="Recent citations" testId="cit-list-panel">
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Citation</th><th className={thCls}>Case</th><th className={thCls}>Violator</th><th className={thCls}>Violation</th><th className={thCls}>Fine</th><th className={thCls}>Court Date</th><th className={thCls}></th></tr></thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className={`${tdCls} font-mono text-xs font-bold text-ujms`}>{c.citation_number}
                    {c.status === "PAID" && <div className="text-[10px] font-bold text-emerald-700">PAID</div>}
                    {c.status === "FTA" && <div className="inline-block mt-0.5 text-[10px] font-bold bg-red-700 text-white rounded-sm px-1.5 py-0.5" data-testid={`cit-fta-${c.id}`} title={`Flagged ${String(c.fta_flagged_at || "").slice(0, 10)}`}>FAILURE TO APPEAR</div>}</td>
                  <td className={`${tdCls} font-mono text-xs`}><Link className="text-blue-900 hover:underline" to={`/cases/${c.case_id}`}>{c.case_number}</Link></td>
                  <td className={tdCls}>{c.first_name} {c.last_name}</td>
                  <td className={`${tdCls} text-xs`}>{c.violation_code} {c.violation_label}</td>
                  <td className={`${tdCls} font-mono text-xs`}>{c.fine ? `$${Number(c.fine).toFixed(2)}` : "Court"}</td>
                  <td className={`${tdCls} font-mono text-xs`}>{c.court_date || "—"}</td>
                  <td className={tdCls}>
                    <button className={btnGhost} onClick={() => ujmsOpenCitationTicket(c.id, c.citation_number)} data-testid={`cit-ticket-${c.id}`}>
                      <Printer className="w-3.5 h-3.5" /> Ticket</button>
                  </td>
                </tr>
              ))}
              {!list.length && <tr><td colSpan={7} className={tdCls} data-testid="cit-empty">No citations filed yet.</td></tr>}
            </tbody>
          </table>
        </Panel>
      </div>
    </div>
  );
}
